export type ConversionResult =
  | { ok: true; text: string }
  | { ok: false; message: string };

const ONES: string[] = ["", "ett", "två", "tre", "fyra", "fem", "sex", "sju", "åtta", "nio"];

const TEENS: string[] = [
  "tio",
  "elva",
  "tolv",
  "tretton",
  "fjorton",
  "femton",
  "sexton",
  "sjutton",
  "arton",
  "nitton",
];

const TENS: string[] = ["", "", "tjugo", "trettio", "fyrtio", "femtio", "sextio", "sjuttio", "åttio", "nittio"];

const DIGITS: string[] = ["noll", ...ONES.slice(1)];

// [singular, plural] for 10^6, 10^9, ... 10^24
const SCALES: [string, string][] = [
  ["miljon", "miljoner"],
  ["miljard", "miljarder"],
  ["biljon", "biljoner"],
  ["biljard", "biljarder"],
  ["triljon", "triljoner"],
  ["triljard", "triljarder"],
  ["kvadriljon", "kvadriljoner"],
];

const MAX_INTEGER_DIGITS = 3 * (SCALES.length + 2);

// 1-999 as a single word, e.g. 123 -> "etthundratjugotre"
function belowThousand(n: number): string {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  let word = hundreds ? ONES[hundreds] + "hundra" : "";
  if (rest >= 20) word += TENS[Math.floor(rest / 10)] + ONES[rest % 10];
  else if (rest >= 10) word += TEENS[rest - 10];
  else word += ONES[rest];
  return word;
}

function integerToWords(digits: string): string {
  if (digits === "") return "noll";

  const groups: number[] = [];
  for (let end = digits.length; end > 0; end -= 3) {
    groups.unshift(Number(digits.slice(Math.max(0, end - 3), end)));
  }

  const words: string[] = [];
  for (let i = 0; i < groups.length - 2; i++) {
    const n = groups[i];
    if (n === 0) continue;
    const [singular, plural] = SCALES[groups.length - 3 - i];
    // "miljon" etc. are en-words: "en miljon", "tjugoen miljoner"
    const count = belowThousand(n).replace(/ett$/, "en");
    words.push(`${count} ${n === 1 ? singular : plural}`);
  }

  // Numbers below a million are written as one word
  const thousands = groups.length >= 2 ? groups[groups.length - 2] : 0;
  const units = groups[groups.length - 1];
  let belowMillion = "";
  if (thousands) belowMillion += (belowThousand(thousands) + "tusen").replace("etttusen", "ettusen");
  if (units) belowMillion += belowThousand(units);
  if (belowMillion) words.push(belowMillion);

  return words.join(" ");
}

export function numberToWords(input: string): ConversionResult {
  // Spaces are thousand separators; both "," and "." are accepted as decimal separator
  const match = input.replace(/\s/g, "").match(/^(-?)(\d*)(?:[.,](\d*))?$/);
  if (!match || (match[2] === "" && !match[3])) {
    return { ok: false, message: "Ange ett nummer" };
  }

  const [, sign, integerPart, decimalPart = ""] = match;
  const integerDigits = integerPart.replace(/^0+/, "");
  if (integerDigits.length > MAX_INTEGER_DIGITS) {
    return { ok: false, message: "För stort :(" };
  }

  let text = integerToWords(integerDigits);
  if (decimalPart) {
    text += " komma " + decimalPart.split("").map((d) => DIGITS[Number(d)]).join(" ");
  }
  if (sign && /[1-9]/.test(integerDigits + decimalPart)) {
    text = "minus " + text;
  }
  return { ok: true, text };
}
